import { Link } from 'react-router-dom';
import { ArrowRight, Check, Globe, Zap, Shield, Smartphone } from 'lucide-react';
import { Button } from '@mawkaa/ui';

const features = [
  {
    icon: Zap,
    title: 'Rapide à créer',
    description: 'Créez votre site en moins de 15 minutes avec notre éditeur intuitif.',
  },
  {
    icon: Smartphone,
    title: 'Responsive',
    description: 'Votre site s\'adapte automatiquement à tous les écrans.',
  },
  {
    icon: Globe,
    title: 'Hébergement inclus',
    description: 'Votre site est hébergé sur notre infrastructure sécurisée.',
  },
  {
    icon: Shield,
    title: 'Sécurisé',
    description: 'SSL gratuit et protection contre les attaques.',
  },
];

const plans = [
  {
    name: 'Gratuit',
    price: '0',
    description: 'Parfait pour démarrer',
    features: ['1 site', 'Sous-domaine mawkaa.tn', 'Templates de base', 'Support email'],
    cta: 'Commencer gratuitement',
    highlighted: false,
  },
  {
    name: 'Starter',
    price: '29',
    description: 'Pour les entrepreneurs',
    features: ['3 sites', 'Domaine personnalisé', 'Tous les templates', 'Support prioritaire', 'Analytics de base'],
    cta: 'Essai gratuit 14 jours',
    highlighted: true,
  },
  {
    name: 'Business',
    price: '79',
    description: 'Pour les entreprises',
    features: ['10 sites', 'Domaine personnalisé', 'Templates premium', 'Support 24/7', 'Analytics avancés', 'E-commerce'],
    cta: 'Essai gratuit 14 jours',
    highlighted: false,
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <span className="text-xl font-bold">Mawkaa</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm hover:text-primary">Fonctionnalités</a>
            <a href="#pricing" className="text-sm hover:text-primary">Tarifs</a>
            <a href="#templates" className="text-sm hover:text-primary">Templates</a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button>Créer un compte</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-primary/5 to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Créez votre site web
            <br />
            <span className="text-primary">en 15 minutes</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Mawkaa est le site builder no-code pour les entrepreneurs tunisiens.
            Pas besoin de compétences techniques.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto">
                Commencer gratuitement
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="#templates">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Voir les templates
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Aucune carte de crédit requise
          </p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Tout ce dont vous avez besoin
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            Tarifs simples et transparents
          </h2>
          <p className="text-muted-foreground text-center mb-12">
            Choisissez le plan qui correspond à vos besoins
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`bg-white rounded-2xl p-8 ${
                  plan.highlighted ? 'ring-2 ring-primary shadow-lg' : 'border'
                }`}
              >
                {plan.highlighted && (
                  <span className="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
                    Populaire
                  </span>
                )}
                <h3 className="text-xl font-bold mt-4">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground"> TND/mois</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button
                    className="w-full"
                    variant={plan.highlighted ? 'default' : 'outline'}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Prêt à créer votre site ?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Rejoignez des milliers d'entrepreneurs tunisiens qui font confiance à Mawkaa
            pour leur présence en ligne.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary">
              Créer mon site gratuitement
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">M</span>
              </div>
              <span className="font-bold">Mawkaa</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Mawkaa. Tous droits réservés.
            </p>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <a href="#" className="hover:text-primary">Conditions</a>
              <a href="#" className="hover:text-primary">Confidentialité</a>
              <a href="#" className="hover:text-primary">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
